import { observable, action, computed, makeObservable, runInAction } from 'mobx'
import dayjs from 'dayjs'
import { Order } from '../Types/Order'
import { OrderItem } from '../Types/OrderItem'
import { FilterStatus } from '../Types/Enums'
import { FetchStatus, FetchResult } from '~shared/lib/Types/FetchTypes'
import { OfflineDevice } from '../Types/SiteHealth'
import { getApiErrorMessage } from '~shared/util/apiErrorMessage'
import {
  fetchOrders as fetchOrdersApi,
  fetchDepartmentAvailability as fetchDepartmentAvailabilityApi,
  updateDepartmentAvailability as updateDepartmentAvailabilityApi,
  editOrder as editOrderApi,
  cancelOrder as cancelOrderApi,
} from '../Services/OrderService'
import { fetchSiteHealth as fetchSiteHealthApi } from '../Services/DeviceService'
import KdsAuthorizationStore from './KdsAuthorizationStore'
import { KdsActionId } from '../Types/KdsAuthorization'
import { KdsAuthorizationCancelledError } from '../Types/Authorization'

class KdsStore {
  ordersFetch: FetchResult<Order[]> = {
    status: FetchStatus.unloaded,
    lastFetch: dayjs().format(),
    data: [],
  }

  departmentAvailable = false

  currentFilter = FilterStatus.Active

  unconfirmedActiveCount = 0

  unconfirmedUpcomingCount = 0

  newActiveCount = 0

  initialLoad = true

  siteHealthFetch: FetchResult<OfflineDevice[]> = {
    status: FetchStatus.unloaded,
    lastFetch: dayjs().format(),
    data: [],
  }

  constructor() {
    makeObservable(this, {
      ordersFetch: observable,
      currentFilter: observable,
      initialLoad: observable,
      departmentAvailable: observable,
      unconfirmedActiveCount: observable,
      unconfirmedUpcomingCount: observable,
      newActiveCount: observable,
      siteHealthFetch: observable,
      fetchOrders: action.bound,
      editOrderStatus: action.bound,
      setFilter: action.bound,
      updateOrder: action.bound,
      filteredOrders: computed,
      hasNewOrders: computed,
      offlineDeviceCount: computed,
      numberOfActiveOrdersRequiringConfirmation: computed,
      numberOfUpcomingOrdersRequiringConfirmation: computed,
      updateDepartmentAvailability: action.bound,
      fetchDepartmentAvailability: action.bound,
      fetchSiteHealth: action.bound,
      cancelCustomerOrder: action.bound,
    })
  }

  async fetchOrders() {
    this.ordersFetch = {
      ...this.ordersFetch,
      status: FetchStatus.fetching,
      lastFetch: dayjs().format(),
    }

    try {
      const statusSegment = this.currentFilter.toLowerCase()
      const data = await fetchOrdersApi(statusSegment)

      runInAction(() => {
        this.ordersFetch = {
          status: FetchStatus.success,
          lastFetch: dayjs().format(),
          data: data.orders,
          error: undefined,
        }

        this.unconfirmedActiveCount = data.unconfirmedActiveCount || 0
        this.unconfirmedUpcomingCount = data.unconfirmedUpcomingCount || 0
        this.newActiveCount = data.newActiveCount || 0

        if (this.initialLoad) {
          this.initialLoad = false
        }
      })
    } catch (error) {
      console.error('Failed to fetch orders:', error)
      runInAction(() => {
        this.ordersFetch = {
          ...this.ordersFetch,
          status: FetchStatus.error,
          error: getApiErrorMessage(error),
        }

        if (this.initialLoad) {
          this.initialLoad = false
        }
      })
    }
  }

  fetchDepartmentAvailability = async () => {
    try {
      const data = await fetchDepartmentAvailabilityApi()

      runInAction(() => {
        this.departmentAvailable = data ?? false
      })
    } catch (error) {
      console.error('Failed to fetch department availability:', error)
    }
  }

  /**
   * Updates department availability through the shared authorization flow. When the config marks
   * `SetDepartmentAvailability` as elevated the operator is prompted for a PIN and the change is
   * submitted with the step-up JWT; otherwise it runs on the device token exactly as before.
   * Returns true when availability changed, false when the operator dismissed the prompt.
   * Throws on a genuine failure (e.g. step-up 401/403 or transport error).
   */
  async updateDepartmentAvailability(departmentAvailable: boolean): Promise<boolean> {
    try {
      await KdsAuthorizationStore.runConfiguredAction(
        KdsActionId.SetDepartmentAvailability,
        () => updateDepartmentAvailabilityApi(departmentAvailable),
        authorization => updateDepartmentAvailabilityApi(departmentAvailable, authorization.token)
      )
      await this.fetchDepartmentAvailability()
      return true
    } catch (error) {
      if (error instanceof KdsAuthorizationCancelledError) {
        return false
      }
      console.error('Failed to update department availability:', error)
      throw error
    }
  }

  async editOrderStatus(orderId: string, newStatus: string): Promise<void> {
    await editOrderApi(orderId, { status: newStatus })

    await this.fetchOrders()
  }

  /**
   * Centralized method to handle all order updates including status changes and weight confirmations
   *
   * @param orderId - The ID of the order to update
   * @param updatedItems - The updated items (empty array for status-only changes)
   * @param targetStatus - The target status to set for the order (null if no status change)
   * @returns {Promise<void>}
   */
  async updateOrder(
    orderId: string,
    updatedItems: OrderItem[],
    targetStatus: string | null
  ): Promise<void> {
    const order = this.ordersFetch.data?.find(o => o.id === orderId)

    if (!order) {
      throw new Error('Order not found')
    }

    await editOrderApi(orderId, { status: targetStatus, items: updatedItems })

    await this.fetchOrders()
  }

  /**
   * Cancels via the shared step-up flow: a PIN when the config marks the action elevated, the
   * device token otherwise. Returns false when the operator dismisses the prompt; throws on a
   * genuine failure (e.g. step-up 401/403).
   */
  async cancelCustomerOrder(orderUid: string): Promise<boolean> {
    try {
      await KdsAuthorizationStore.runConfiguredAction(
        KdsActionId.CancelCustomerOrders,
        () => cancelOrderApi(orderUid),
        authorization => cancelOrderApi(orderUid, authorization.token)
      )
      return true
    } catch (error) {
      if (error instanceof KdsAuthorizationCancelledError) {
        return false
      }
      throw error
    }
  }

  setFilter(filter: FilterStatus) {
    this.currentFilter = filter
  }

  async fetchSiteHealth(): Promise<void> {
    this.siteHealthFetch = {
      ...this.siteHealthFetch,
      status: FetchStatus.fetching,
      lastFetch: dayjs().format(),
    }

    try {
      const data = await fetchSiteHealthApi()

      runInAction(() => {
        this.siteHealthFetch = {
          status: FetchStatus.success,
          lastFetch: dayjs().format(),
          data: data?.offlineDevices ?? [],
        }
      })
    } catch (error) {
      console.error('Failed to fetch site health:', error)
      runInAction(() => {
        this.siteHealthFetch = {
          ...this.siteHealthFetch,
          status: FetchStatus.error,
        }
      })
    }
  }

  get offlineDeviceCount(): number {
    return this.siteHealthFetch.data?.length ?? 0
  }

  get filteredOrders(): Order[] {
    const orders = this.ordersFetch.data ?? []
    return orders
  }

  get hasNewOrders(): boolean {
    return this.newActiveCount > 0
  }

  get numberOfActiveOrdersRequiringConfirmation(): number {
    return this.unconfirmedActiveCount
  }

  get numberOfUpcomingOrdersRequiringConfirmation(): number {
    return this.unconfirmedUpcomingCount
  }
}

const kdsStore = new KdsStore()
export default kdsStore
export { KdsStore }
